import React, { useEffect, useRef, useState } from "react";
import api, { formatApiError, API } from "@/lib/api";
import { PageHeader } from "@/components/PageHeader";
import { PageLoader, EmptyState } from "@/components/Loading";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Folder, FolderPlus, Upload, Trash2, Pencil, Move, FileText, Images, ChevronLeft, Download, Loader2, Home } from "lucide-react";
import { toast } from "sonner";

const fmtSize = (b) => { if (!b) return "0 ب"; const u = ["ب", "ك.ب", "م.ب", "ج.ب"]; let i = 0; let n = b; while (n >= 1024 && i < 3) { n /= 1024; i++; } return `${n.toFixed(n < 10 && i > 0 ? 1 : 0)} ${u[i]}`; };
const extOf = (name) => (name || "").split(".").pop().toLowerCase();
const isImg = (n) => ["jpg", "jpeg", "png", "webp"].includes(extOf(n));
const isVid = (n) => ["mp4", "mov"].includes(extOf(n));
const token = () => localStorage.getItem("token");
const fileUrl = (id) => `${API}/album/files/${id}/download?auth=${token()}`;

export default function Album() {
  const [folderId, setFolderId] = useState(null);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [newFolderOpen, setNewFolderOpen] = useState(false);
  const [folderName, setFolderName] = useState("");
  const [uploading, setUploading] = useState(false);
  const [moveItem, setMoveItem] = useState(null);
  const fileRef = useRef(null);

  const load = (fid = folderId) => {
    setLoading(true);
    api.get(`/album${fid ? `?folder_id=${fid}` : ""}`).then((r) => setData(r.data)).catch(() => setData(null)).finally(() => setLoading(false));
  };
  useEffect(() => { load(folderId); }, [folderId]);

  const createFolder = async () => {
    if (!folderName.trim()) return;
    try { await api.post("/album/folders", { name: folderName.trim(), parent_id: folderId }); setNewFolderOpen(false); setFolderName(""); load(); toast.success("تم إنشاء المجلد"); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const onUpload = async (e) => {
    const f = e.target.files?.[0]; if (!f) return;
    setUploading(true);
    const fd = new FormData(); fd.append("file", f); if (folderId) fd.append("folder_id", folderId);
    try { await api.post("/album/upload", fd, { headers: { "Content-Type": "multipart/form-data" } }); toast.success("تم رفع الملف"); load(); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setUploading(false); if (fileRef.current) fileRef.current.value = ""; }
  };

  const renameItem = async (type, item) => {
    const name = window.prompt("الاسم الجديد", item.name); if (!name || !name.trim()) return;
    try { await api.put(`/album/${type}/${item.id}`, { name: name.trim() }); load(); toast.success("تم"); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const deleteItem = async (type, item) => {
    if (!window.confirm(type === "folders" ? "حذف المجلد وكل ما بداخله؟" : "حذف الملف؟")) return;
    try { await api.delete(`/album/${type}/${item.id}`); load(); toast.success("تم الحذف"); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  if (loading && !data) return <PageLoader />;
  if (!data) return <EmptyState icon={Images} title="تعذّر تحميل الألبوم" desc="حاول تحديث الصفحة." />;
  const usedPct = Math.min(100, Math.round((data.usage.used / data.usage.quota) * 100));

  return (
    <>
      <PageHeader title="ألبوم المنصة" subtitle="مكتبة وسائط مدينة حلب (30 جيجابايت)"
        action={
          <div className="flex gap-2">
            <Button data-testid="new-folder-btn" onClick={() => setNewFolderOpen(true)} variant="outline" className="rounded-xl"><FolderPlus className="me-2 h-4 w-4" /> مجلد جديد</Button>
            <Button data-testid="upload-btn" onClick={() => fileRef.current?.click()} disabled={uploading} className="rounded-xl bg-navy hover:bg-navy-light">{uploading ? <Loader2 className="me-2 h-4 w-4 animate-spin" /> : <Upload className="me-2 h-4 w-4" />} رفع ملف</Button>
            <input ref={fileRef} type="file" data-testid="file-input" onChange={onUpload} className="hidden"
              accept=".jpg,.jpeg,.png,.webp,.mp4,.mov,.mp3,.wav,.pdf,.doc,.docx,.txt,.xls,.xlsx,.csv,.ppt,.pptx,.zip,.rar" />
          </div>
        }
      />

      {/* Usage bar */}
      <div className="mb-5 rounded-2xl border border-slate-100 bg-card p-4 soft-shadow">
        <div className="mb-1 flex justify-between text-xs text-slate-500"><span>المساحة المستخدمة</span><span data-testid="usage-text">{fmtSize(data.usage.used)} من 30 ج.ب</span></div>
        <div className="h-2.5 overflow-hidden rounded-full bg-slate-100"><div className="h-full rounded-full bg-greenx transition-all" style={{ width: `${usedPct}%` }} /></div>
      </div>

      {/* Breadcrumb */}
      <div className="mb-4 flex flex-wrap items-center gap-1 text-sm">
        <button data-testid="crumb-root" onClick={() => setFolderId(null)} className="flex items-center gap-1 rounded-lg px-2 py-1 font-semibold text-navy hover:bg-slate-100"><Home className="h-4 w-4" /> الرئيسية</button>
        {data.breadcrumb.map((b) => (
          <span key={b.id} className="flex items-center gap-1">
            <ChevronLeft className="h-4 w-4 text-slate-300" />
            <button onClick={() => setFolderId(b.id)} className="rounded-lg px-2 py-1 text-slate-600 hover:bg-slate-100">{b.name}</button>
          </span>
        ))}
      </div>

      {data.folders.length === 0 && data.files.length === 0 ? (
        <EmptyState icon={Images} title="المجلد فارغ" desc="أنشئ مجلدًا أو ارفع ملفًا لتبدأ." />
      ) : (
        <div className="space-y-6">
          {data.folders.length > 0 && (
            <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
              {data.folders.map((f) => (
                <div key={f.id} data-testid={`folder-${f.id}`} className="group relative rounded-2xl border border-slate-100 bg-card p-4 soft-shadow card-hover">
                  <button onClick={() => setFolderId(f.id)} className="flex w-full items-center gap-3 text-start">
                    <div className="flex h-11 w-11 items-center justify-center rounded-xl bg-amber-100 text-amber-600"><Folder className="h-6 w-6" /></div>
                    <span className="truncate font-semibold text-navy">{f.name}</span>
                  </button>
                  <div className="absolute left-2 top-2 flex gap-1 opacity-0 transition-opacity group-hover:opacity-100">
                    <IconBtn testid={`ren-folder-${f.id}`} onClick={() => renameItem("folders", f)} icon={Pencil} />
                    <IconBtn testid={`mv-folder-${f.id}`} onClick={() => setMoveItem({ type: "folders", item: f })} icon={Move} />
                    <IconBtn testid={`del-folder-${f.id}`} onClick={() => deleteItem("folders", f)} icon={Trash2} danger />
                  </div>
                </div>
              ))}
            </div>
          )}

          {data.files.length > 0 && (
            <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
              {data.files.map((f) => (
                <div key={f.id} data-testid={`file-${f.id}`} className="group relative overflow-hidden rounded-2xl border border-slate-100 bg-card soft-shadow card-hover">
                  <div className="flex h-36 items-center justify-center bg-slate-50">
                    {isImg(f.name) ? <img src={fileUrl(f.id)} alt={f.name} className="h-full w-full object-cover" />
                      : isVid(f.name) ? <video src={fileUrl(f.id)} className="h-full w-full object-cover" muted />
                      : <FileText className="h-12 w-12 text-slate-300" />}
                    <span className="absolute right-2 top-2 rounded-md bg-navy/80 px-1.5 py-0.5 text-[10px] font-bold uppercase text-white">{extOf(f.name)}</span>
                  </div>
                  <div className="p-3">
                    <p className="truncate text-sm font-semibold text-navy">{f.name}</p>
                    <p className="text-xs text-slate-400">{fmtSize(f.size)}</p>
                    <div className="mt-2 flex gap-1">
                      <a href={fileUrl(f.id)} target="_blank" rel="noreferrer" download data-testid={`dl-${f.id}`} className="flex h-8 w-8 items-center justify-center rounded-lg text-slate-500 hover:bg-slate-100"><Download className="h-4 w-4" /></a>
                      <IconBtn testid={`ren-file-${f.id}`} onClick={() => renameItem("files", f)} icon={Pencil} />
                      <IconBtn testid={`mv-file-${f.id}`} onClick={() => setMoveItem({ type: "files", item: f })} icon={Move} />
                      <IconBtn testid={`del-file-${f.id}`} onClick={() => deleteItem("files", f)} icon={Trash2} danger />
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      <Dialog open={newFolderOpen} onOpenChange={setNewFolderOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle className="font-heading text-navy">مجلد جديد</DialogTitle></DialogHeader>
          <div><Label>اسم المجلد</Label><Input data-testid="folder-name-input" value={folderName} onChange={(e) => setFolderName(e.target.value)} className="mt-1.5 rounded-lg" onKeyDown={(e) => e.key === "Enter" && createFolder()} /></div>
          <DialogFooter><Button data-testid="folder-create" onClick={createFolder} disabled={!folderName.trim()} className="rounded-xl bg-navy hover:bg-navy-light">إنشاء</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      {moveItem && <MoveDialog moveItem={moveItem} onClose={() => setMoveItem(null)} onDone={() => { setMoveItem(null); load(); }} />}
    </>
  );
}

function IconBtn({ onClick, icon: Icon, danger, testid }) {
  return <button data-testid={testid} onClick={onClick} className={`flex h-8 w-8 items-center justify-center rounded-lg bg-white/90 ${danger ? "text-red-400 hover:bg-red-50 hover:text-red-500" : "text-slate-500 hover:bg-slate-100"}`}><Icon className="h-4 w-4" /></button>;
}

function MoveDialog({ moveItem, onClose, onDone }) {
  const [folders, setFolders] = useState(null);
  useEffect(() => { api.get("/album/folders/all").then((r) => setFolders(r.data)).catch(() => setFolders([])); }, []);
  const { type, item } = moveItem;
  const move = async (dest) => {
    const key = type === "folders" ? "parent_id" : "folder_id";
    try { await api.put(`/album/${type}/${item.id}`, { [key]: dest }); toast.success("تم النقل"); onDone(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const options = (folders || []).filter((f) => f.id !== item.id);
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-h-[80vh] overflow-y-auto">
        <DialogHeader><DialogTitle className="font-heading text-navy">نقل «{item.name}» إلى</DialogTitle></DialogHeader>
        {folders === null ? <div className="flex justify-center py-6"><Loader2 className="h-6 w-6 animate-spin text-slate-300" /></div> : (
          <div className="space-y-1">
            <button data-testid="move-to-root" onClick={() => move(null)} className="flex w-full items-center gap-2 rounded-lg p-2.5 text-start hover:bg-slate-50"><Home className="h-4 w-4 text-navy" /> الرئيسية</button>
            {options.map((f) => (
              <button key={f.id} data-testid={`move-to-${f.id}`} onClick={() => move(f.id)} className="flex w-full items-center gap-2 rounded-lg p-2.5 text-start hover:bg-slate-50"><Folder className="h-4 w-4 text-amber-500" /> {f.name}</button>
            ))}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
